import React from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from "../images/hotel_logo.png";
import { HOTEL_NAME } from "../Variables.js";
import { useUserAuth } from "../context/UserAuthContext";

const ExpenditurePDFGenerator = ({ expenditures, days, month, buttonText }) => {
  const { user, userData } = useUserAuth();
  const hotelName = `${HOTEL_NAME}`;

  const formatDate = (date) => {
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateForFilename = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDateTime = (date) => {
    return date
      .toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(",", "");
  };

  const formatAmount = (amount) => {
    return `N${amount.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  const getPeriodLabel = () => {
    if (month) {
      const [year, monthNum] = month.split("-");
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      return `${months[parseInt(monthNum) - 1]} ${year}`;
    }
    return buttonText;
  };

  const addHeader = (doc, currentY = 10) => {
    // Adding logo
    doc.addImage(Logo, "PNG", 14, currentY, 15, 15);

    // Adding store name
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(hotelName, 35, currentY + 5);

    // Adding document title
    doc.setFontSize(16);
    doc.setFont("normal");
    doc.text(`Expenditure Report - ${getPeriodLabel()}`, 35, currentY + 13);

    // Adding generated by and timestamp
    doc.setFontSize(10);
    const fullName = userData?.firstName && userData?.lastName
      ? `${userData.firstName} ${userData.lastName}`
      : user?.displayName || "Unknown";
    const generatedBy = `Generated by: ${fullName}`;
    const generatedAt = `Generated on: ${formatDateTime(new Date())}`;
    doc.text(generatedBy, 35, currentY + 18);
    doc.text(generatedAt, 35, currentY + 23);
    currentY += 27;
    return currentY;
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    let currentY = addHeader(doc, 10);

    let filteredExpenditures = expenditures;

    // Filter by month if provided
    if (month) {
      const [year, monthNum] = month.split("-").map(Number);
      filteredExpenditures = expenditures.filter((exp) => {
        const expDate = exp.date?.toDate ? exp.date.toDate() : new Date(0);
        return (
          expDate.getFullYear() === year && expDate.getMonth() + 1 === monthNum
        );
      });
    } else {
      // Filter by days if month is not provided
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);
      filteredExpenditures = expenditures.filter((exp) => {
        const expDate = exp.date?.toDate ? exp.date.toDate() : new Date(0);
        return expDate >= cutoffDate;
      });
    }

    // Sort by date (descending)
    filteredExpenditures.sort((a, b) => b.date.toDate().getTime() - a.date.toDate().getTime());

    // Prepare table data
    const tableData = filteredExpenditures.map((exp, index) => [
      (index + 1).toString(),
      exp.description || "Unknown",
      formatAmount(exp.amount || 0),
      formatDate(exp.date?.toDate ? exp.date.toDate() : new Date(0)),
      exp.addedByName || "Unknown",
      exp.addedByPhone || "N/A",
    ]);

    // Generate expenditure table
    autoTable(doc, {
      startY: currentY,
      head: [
        ["S/N", "Description", "Amount", "Date", "Added By", "Phone"],
      ],
      body: tableData,
      theme: "striped",
      headStyles: { fillColor: [41, 128, 185] },
      bodyStyles: { textColor: [0, 0, 0] },
      margin: { left: 14, right: 10 },
      styles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 15 }, // S/N
        1: { cellWidth: 60 }, // Description
        2: { cellWidth: 20 }, // Amount
        3: { cellWidth: 25 }, // Date
        4: { cellWidth: 30 }, // Added By
        5: { cellWidth: 25 }, // Phone
      },
      didDrawPage: (data) => {
        currentY = data.table.finalY || data.cursor.y;
        // Add header on new pages if content overflows
        if (data.pageNumber < doc.internal.getNumberOfPages()) {
          doc.setPage(data.pageNumber + 1);
          currentY = addHeader(doc, 10);
        }
      },
    });

    // Calculate total expenditure
    const totalExpenditure = filteredExpenditures.reduce(
      (sum, exp) => sum + (exp.amount || 0),
      0
    );

    // Add total
    currentY += 10;
    doc.setFontSize(12);
    doc.text(`Total Expenditure: ${formatAmount(totalExpenditure)}`, 14, currentY);

    // Add footer with page number
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width - 30,
        doc.internal.pageSize.height - 10
      );
    }

    // Save the PDF
    const filename = month
      ? `expenditure_report_${month}.pdf`
      : `expenditure_report_${days}_days_${formatDateForFilename(new Date())}.pdf`;
    doc.save(filename);
  };

  return (
    <button className="login-button login-button-d login-button-report caps" onClick={generatePDF}>
      {buttonText}
    </button>
  );
};

export default ExpenditurePDFGenerator;